import type { IUserLoginParams, IUserSmsLoginParams, IUserState } from "./model";
import { defineStore } from "pinia";
import {
  changePassword,
  getCustomerIdBySubdomain,
  getUserInfo,
  getWarehouseListByCustomerId,
  login,
  phoneLogin,
} from "./api";

const initUserInfo = {
  id: "",
  account: "",
  loginTime: "",
};

let pendingUserInfoRequest: Promise<void> | null = null;

export const useUserStore = defineStore("user", {
  state: (): IUserState => ({
    token: "",
    customerId: "",
    userInfo: { ...initUserInfo },
    userInfoFetchedAt: 0,
    warehouseList: [],
  }),
  getters: {
    requiresPasswordChange: (state) => state.userInfo.changePwdOnNextLogin === 1,
  },
  actions: {
    async updatePassword(password: string) {
      const token = this.token;
      await changePassword({ password, firstLoginFlag: this.requiresPasswordChange });
      // A completed request must not update a different login session.
      if (this.token === token) {
        this.userInfo.changePwdOnNextLogin = 0;
      }
    },
    async login(loginParams: IUserLoginParams) {
      const res = await login(loginParams);
      this.token = res.data.token;
      this.userInfoFetchedAt = 0;
    },
    async smsLogin(loginParams: IUserSmsLoginParams) {
      const res = await phoneLogin(loginParams);
      this.token = res.data.token;
      this.userInfoFetchedAt = 0;
    },
    async getUserInfo() {
      const res = await getUserInfo();
      this.userInfo = res.data;
      this.customerId = res.data.customerId || this.customerId;
      this.userInfoFetchedAt = Date.now();
    },
    async getCustomerId(subdomain: string) {
      // Resolve the new tenant without leaking the previously persisted tenant
      // into the non-auth request header or keeping its warehouse options visible.
      this.customerId = "";
      this.warehouseList = [];

      const res = await getCustomerIdBySubdomain(subdomain);
      this.customerId = res.data.customerId;
      await this.getWarehouseList();
    },
    async getWarehouseList() {
      if (!this.customerId) {
        this.warehouseList = [];
        return;
      }

      const res = await getWarehouseListByCustomerId(this.customerId);
      this.warehouseList = res.data;
    },
    async refreshUserInfoIfStale(ttl: number) {
      const shouldRefresh = !this.userInfo.id || Date.now() - this.userInfoFetchedAt > ttl;

      if (!shouldRefresh) {
        return;
      }

      pendingUserInfoRequest ||= this.getUserInfo().finally(() => {
        pendingUserInfoRequest = null;
      });

      await pendingUserInfoRequest;
    },
    logout() {
      this.token = "";
      this.customerId = "";
      this.userInfo = { ...initUserInfo };
      this.userInfoFetchedAt = 0;
      this.warehouseList = [];
    },
  },
  persist: {
    key: "user",
    pick: ["token", "customerId"],
  },
});
